import { memo } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * ProductSidebar Component
 *
 * Reusable sidebar for product filtering and category navigation.
 * Used on ProductListing and CategoryPage.
 *
 * @param {Array} categories - List of categories from backend
 * @param {string|null} currentCategorySlug - Currently selected category slug (null for "All Collections")
 * @param {Array} priceRange - [min, max] price range
 * @param {string} sortBy - Current sort option
 * @param {Function} onSortChange - Callback for sort changes
 * @param {Function} onClearFilters - Callback to clear all filters
 * @param {Function} onApplyFilter - Callback to apply filters
 * @param {boolean} filterLoading - Whether filter operation is in progress
 * @param {object} sliderInstance - noUiSlider instance (for resetting slider)
 */
const ProductSidebar = memo(function ProductSidebar({
  categories = [],
  currentCategorySlug = null,
  priceRange = [0, 800],
  sortBy = 'default',
  onSortChange,
  onClearFilters,
  onApplyFilter,
  filterLoading = false,
  sliderInstance = null
}) {
  const navigate = useNavigate();

  // Handler for category selection
  const handleCategoryClick = (categorySlug) => {
    if (categorySlug === null) {
      // Navigate to "All Collections" page
      navigate('/product-listing');
    } else {
      // Navigate to specific category page
      navigate(`/category/${categorySlug}`);
    }
  };

  // Check if filters are active
  const hasActiveFilters = priceRange[0] > 0 || priceRange[1] < 800;

  return (
    <div className="col-12 col-lg-4 order-1 sticky">
      <div id="product-filter-nav" className="product-filter-nav mb-3">
        {/* Category Filter */}
        <div className="product-category" role="navigation" aria-label="Category filters">
          <h5 className="filter-heading text-center text-lg-left">Category</h5>
          <ul className="category-scroll" style={{ maxHeight: '180px', overflowY: 'scroll' }}>
            {categories.length > 0 ? (
              <>
                {/* "All Collections" option */}
                <li
                  role="button"
                  tabIndex={0}
                  onClick={() => handleCategoryClick(null)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleCategoryClick(null);
                    }
                  }}
                  aria-pressed={currentCategorySlug === null}
                  aria-label="Show all collections"
                  style={{
                    fontWeight: currentCategorySlug === null ? 'bold' : 'normal',
                    color: currentCategorySlug === null ? '#03a9f5' : 'inherit',
                    backgroundColor: currentCategorySlug === null ? 'rgba(3, 169, 245, 0.1)' : 'transparent',
                    borderLeft: currentCategorySlug === null ? '3px solid #03a9f5' : '3px solid transparent',
                    padding: '8px 12px',
                    marginLeft: '-12px',
                    marginRight: '-12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <span>All Collections</span>
                </li>

                {/* Category list */}
                {categories.map((category) => (
                  <li
                    key={category.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleCategoryClick(category.slug)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCategoryClick(category.slug);
                      }
                    }}
                    aria-pressed={currentCategorySlug === category.slug}
                    aria-label={`Filter by ${category.name}${category.productCount !== undefined ? `, ${category.productCount} products` : ''}`}
                    style={{
                      fontWeight: currentCategorySlug === category.slug ? 'bold' : 'normal',
                      color: currentCategorySlug === category.slug ? '#03a9f5' : 'inherit',
                      backgroundColor: currentCategorySlug === category.slug ? 'rgba(3, 169, 245, 0.1)' : 'transparent',
                      borderLeft: currentCategorySlug === category.slug ? '3px solid #03a9f5' : '3px solid transparent',
                      padding: '8px 12px',
                      marginLeft: '-12px',
                      marginRight: '-12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <span>{category.name}</span>
                    {category.productCount !== undefined && (
                      <span> ({category.productCount})</span>
                    )}
                  </li>
                ))}
              </>
            ) : (
              <li className="text-muted" style={{cursor: 'default'}}>Loading categories...</li>
            )}
          </ul>
        </div>

        {/* Sort Options */}
        <div className="product-sort mt-4" role="radiogroup" aria-label="Sort options">
          <h5 className="filter-heading text-center text-lg-left">Sort By</h5>
          <div className="sort-options">
            {[
              { value: 'default', label: 'Featured', icon: 'fa-star' },
              { value: 'price_asc', label: 'Price: Low to High', icon: 'fa-arrow-up' },
              { value: 'price_desc', label: 'Price: High to Low', icon: 'fa-arrow-down' },
              { value: 'name_asc', label: 'Name: A to Z', icon: 'fa-sort-alpha-down' },
              { value: 'newest', label: 'Newest First', icon: 'fa-clock' }
            ].map(option => (
              <label
                key={option.value}
                className={`sort-option ${sortBy === option.value ? 'active' : ''}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px 12px',
                  marginBottom: '8px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  border: sortBy === option.value ? '2px solid #03a9f5' : '2px solid transparent',
                  backgroundColor: sortBy === option.value ? 'rgba(3, 169, 245, 0.1)' : 'transparent',
                  transition: 'all 0.2s ease'
                }}
              >
                <input
                  type="radio"
                  name="sort"
                  value={option.value}
                  checked={sortBy === option.value}
                  onChange={(e) => onSortChange(e.target.value)}
                  style={{ marginRight: '10px' }}
                />
                <i className={`fas ${option.icon}`} style={{ marginRight: '8px', color: sortBy === option.value ? '#03a9f5' : 'inherit' }}></i>
                <span style={{ fontWeight: sortBy === option.value ? '600' : 'normal' }}>{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Price Range Filter */}
        <div className="product-price mt-1">
          <h5 className="filter-heading">Shop By</h5>
          <div id="slider-range"></div>
          <p className="price-num" style={{color: '#0b2e13'}}>
            Price: <span id="min-p"></span> <span id="max-p"></span>
          </p>
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <div className="text-center mb-2">
            <button
              className="btn btn-link btn-sm"
              onClick={onClearFilters}
              style={{ textDecoration: 'none', fontSize: '14px' }}
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Apply Filter Button */}
        <button
          className="btn pink-gradient-btn-into-black mt-1"
          onClick={onApplyFilter}
          disabled={filterLoading}
          aria-label="Apply filters to products"
          aria-busy={filterLoading}
        >
          {filterLoading ? 'Filtering...' : 'Filter'}
        </button>
      </div>
    </div>
  );
});

export default ProductSidebar;
